// SharedColumnsView.tsx — Geteilte Ansicht des Kalender-Neubaus (`/termine`, SP5,
// Technik A): N nebeneinander gerenderte RBC-Day-Instanzen (je ein
// OutlookKalender im Day-Modus), horizontal scrollbar, mit synchronisierter
// vertikaler Zeitachse. Die erste Spalte zeigt die Zeit-Gutter; bei den weiteren
// Spalten wird die Gutter per CSS (.cal-shared-rest) ausgeblendet, sodass eine
// gemeinsame Zeitachse links steht.
//
// Per-Spalte WRITE-Gating: canEditEvent gibt je Spalte `col.canWrite` zurück
// (aus calendarAccess === 'WRITE'). Drag/Resize nur auf WRITE-Spalten; das
// Backend sichert das über assertCalendarWrite doppelt ab. Klick auf ein Event
// (onSelectEvent, SP2) und Reschedule (onEventDrop/onEventResize, SP3) werden
// nur durchgereicht.

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Views } from "react-big-calendar";
import OutlookKalender, { type KalenderEventChange } from "../OutlookKalender";
import { REFINED } from "../risikoanalyseStep/tokens";
import type { CalendarGridEvent } from "./types";
import { useCalendarState } from "./CalendarStateContext";
import { rasterFuerSkala } from "./zeitskala";

export interface SharedColumn {
    address: string;
    displayName: string;
    color: string;
    /** aus calendarAccess === 'WRITE' — steuert Drag/Resize je Spalte. */
    canWrite: boolean;
}

export interface SharedColumnsViewProps {
    columns: SharedColumn[];
    /** Gemeinsamer Tag (aus dem Context). */
    date: Date;
    /** address(lowercased) → Events der Spalte. */
    eventsByColumn: Map<string, CalendarGridEvent[]>;
    /** Einzelklick → Peek-Popover (SP4); 2. Argument = RBC-Klick-Event (Anker). */
    onSelectEvent?: (event: CalendarGridEvent, e?: React.SyntheticEvent) => void;
    /** Doppelklick → direkt das Termin-Fenster (SP4). */
    onDoubleClickEvent?: (event: CalendarGridEvent) => void;
    /** Rechtsklick auf einen Terminblock → Kontextmenü (SP4, A14). */
    onEventContextMenu?: (event: CalendarGridEvent, pos: { x: number; y: number }) => void;
    onEventDrop?: (change: KalenderEventChange) => void;
    onEventResize?: (change: KalenderEventChange) => void;
}

const COL_MIN_WIDTH = 260;

const SharedColumnsView: React.FC<SharedColumnsViewProps> = ({
    columns,
    date,
    eventsByColumn,
    onSelectEvent,
    onDoubleClickEvent,
    onEventContextMenu,
    onEventDrop,
    onEventResize,
}) => {
    // Zeitskala (T-31): EINE Skala für alle Spalten — sonst laufen die
    // Zeitachsen trotz synchronisiertem scrollTop auseinander.
    const { zeitskala } = useCalendarState();
    const raster = useMemo(() => rasterFuerSkala(zeitskala), [zeitskala]);

    // Refs auf die scrollbaren RBC-Zeit-Container je Spalte für den Scroll-Sync.
    const wrapRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scrollEls = useRef<HTMLElement[]>([]);
    const syncing = useRef(false);

    // Nach jedem Render die internen .rbc-time-content-Elemente einsammeln und
    // einen gemeinsamen onScroll-Listener verdrahten. Ref-basiert (kein State pro
    // Scroll-Tick), damit ein Re-Render den Sync nicht zurücksetzt.
    useEffect(() => {
        const els: HTMLElement[] = [];
        const cleanups: (() => void)[] = [];
        wrapRefs.current.forEach((wrap) => {
            if (!wrap) return;
            const content = wrap.querySelector<HTMLElement>(".rbc-time-content");
            if (!content) return;
            els.push(content);
        });
        scrollEls.current = els;

        const onScroll = (e: Event) => {
            if (syncing.current) return;
            const src = e.currentTarget as HTMLElement;
            syncing.current = true;
            for (const el of scrollEls.current) {
                if (el !== src) el.scrollTop = src.scrollTop;
            }
            // im nächsten Frame entsperren (verhindert Sync-Schleifen/Flackern).
            requestAnimationFrame(() => {
                syncing.current = false;
            });
        };

        for (const el of els) {
            el.addEventListener("scroll", onScroll);
            cleanups.push(() => el.removeEventListener("scroll", onScroll));
        }
        return () => cleanups.forEach((fn) => fn());
        // Spalten-Anzahl + Tag ändern das interne Markup → Listener neu binden.
    }, [columns.length, date, raster]);

    const noop = useCallback(() => undefined, []);

    if (columns.length === 0) return null;

    return (
        <div style={rootStyle}>
            <div style={scrollerStyle}>
                {columns.map((col, i) => {
                    const colEvents = eventsByColumn.get(col.address.toLowerCase()) ?? [];
                    return (
                        <div
                            key={col.address}
                            style={{
                                ...colStyle,
                                borderLeft: i === 0 ? "none" : `1px solid ${REFINED.line}`,
                            }}
                        >
                            {/* Spaltenkopf: Farbpunkt + Name (+ read-only-Hinweis). */}
                            <div style={colHeadStyle} title={col.address}>
                                <span
                                    aria-hidden
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        background: col.color,
                                        flex: "0 0 auto",
                                    }}
                                />
                                <span style={colHeadNameStyle}>{col.displayName}</span>
                                {!col.canWrite && <span style={roTagStyle}>nur lesen</span>}
                            </div>
                            <div
                                ref={(el) => {
                                    wrapRefs.current[i] = el;
                                }}
                                // Erste Spalte mit Gutter (Zeitachse), Rest ohne
                                // (gemeinsame Achse links). cal-shared-rest blendet
                                // die RBC-Gutter via calendar.css aus.
                                className={i === 0 ? "cal-shared-first" : "cal-shared-rest"}
                                style={{ flex: 1, minHeight: 0 }}
                            >
                                <OutlookKalender
                                    events={colEvents}
                                    date={date}
                                    view={Views.DAY}
                                    onView={noop}
                                    onNavigate={noop}
                                    toolbar={false}
                                    step={raster.step}
                                    timeslots={raster.timeslots}
                                    stundePx={raster.stundePx}
                                    zeitzeileAbMinuten={raster.zeitzeileAbMinuten}
                                    onSelectEvent={(e, domEvent) =>
                                        onSelectEvent?.(e as CalendarGridEvent, domEvent)
                                    }
                                    onDoubleClickEvent={
                                        onDoubleClickEvent
                                            ? (e) =>
                                                  onDoubleClickEvent(e as CalendarGridEvent)
                                            : undefined
                                    }
                                    onEventContextMenu={
                                        onEventContextMenu
                                            ? (e, pos) =>
                                                  onEventContextMenu(
                                                      e as CalendarGridEvent,
                                                      pos,
                                                  )
                                            : undefined
                                    }
                                    onEventDrop={col.canWrite ? onEventDrop : undefined}
                                    onEventResize={col.canWrite ? onEventResize : undefined}
                                    canEditEvent={() => col.canWrite}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const rootStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
};

const scrollerStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    overflowX: "auto",
    overflowY: "hidden",
};

const colStyle: React.CSSProperties = {
    flex: `1 0 ${COL_MIN_WIDTH}px`,
    minWidth: COL_MIN_WIDTH,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};

const colHeadStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderBottom: `1px solid ${REFINED.line}`,
    background: REFINED.white,
    minHeight: 36,
    boxSizing: "border-box",
};

const colHeadNameStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: REFINED.ink,
    font: `600 13px ${REFINED.fontUI}`,
};

const roTagStyle: React.CSSProperties = {
    padding: "1px 6px",
    borderRadius: 6,
    background: REFINED.fieldBg,
    color: REFINED.ink3,
    font: `600 10px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    flex: "0 0 auto",
};

export default SharedColumnsView;
